import { useState, useCallback } from 'react';
import {
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { TreeNode, findNodeById } from 'shared/domain/checklistTree';

interface UseDragAndDropParams {
  tree: TreeNode[];
  onReorderSection: (
    sectionId: string,
    beforeId?: string,
    afterId?: string,
    parentSectionId?: string | null
  ) => void;
  onReorderItem: (
    itemId: string,
    beforeId?: string,
    afterId?: string,
    parentSectionId?: string | null
  ) => void;
}

interface UseDragAndDropResult {
  activeId: UniqueIdentifier | null;
  overId: UniqueIdentifier | null;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
}

export function useDragAndDrop({
  tree,
  onReorderSection,
  onReorderItem,
}: UseDragAndDropParams): UseDragAndDropResult {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over?.id ?? null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setOverId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const activeNode = findNodeById(tree, String(active.id));
      const overNode = findNodeById(tree, String(over.id));

      if (!activeNode || !overNode) {
        return;
      }

      const siblings = getSiblings(tree, activeNode.parentSectionId);
      const activeIndex = siblings.findIndex((n) => n.id === activeNode.id);
      const overIndex = siblings.findIndex((n) => n.id === overNode.id);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      const reorderedSiblings = arrayMove(siblings, activeIndex, overIndex);
      const beforeNode = reorderedSiblings[overIndex - 1];
      const afterNode = reorderedSiblings[overIndex + 1];

      const beforeId = beforeNode?.id;
      const afterId = afterNode?.id;

      if (activeNode.type === 'section') {
        onReorderSection(
          activeNode.id,
          beforeId,
          afterId,
          activeNode.parentSectionId
        );
      } else {
        onReorderItem(
          activeNode.id,
          beforeId,
          afterId,
          activeNode.parentSectionId
        );
      }
    },
    [tree, onReorderSection, onReorderItem]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setOverId(null);
  }, []);

  return {
    activeId,
    overId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
}

function getSiblings(
  tree: TreeNode[],
  parentSectionId: string | null
): TreeNode[] {
  if (!parentSectionId) {
    return tree;
  }

  const parentNode = findNodeById(tree, parentSectionId);
  if (parentNode && parentNode.type === 'section') {
    return parentNode.children;
  }

  return [];
}
